import { wait } from '@packages/core/async';

import type {
    GetAllDtcsLine,
    GetAllDtcsRequest,
    GetAllDtcsResponse,
    GetConnectionStatusRequest,
    GetConnectionStatusResponse,
    GetDiagramRequest,
    GetDiagramResponse,
    GetFreezeFramesDocumentRequest,
    GetFreezeFramesDocumentResponse,
    GetFreezeFramesErrorResponse,
    GetFreezeFramesFrameResponse,
    GetFreezeFramesParameterResponse,
    GetFreezeFramesReadoutResponse,
    GetFreezeFramesRequest,
    GetFreezeFramesResponse,
    GetFreezeFramesValueResponse,
    GetLastConnectionResponse,
    GetRecentConnectionByVinRequest,
    GetRecentConnectionByVinResponse,
    GetVehicleByCloudConnectorIdLine,
    GetVehicleByCloudConnectorIdRequest,
    GetVehicleByCloudConnectorIdResponse,
    RecentConnectionRecord,
    Ssm5BatteryVoltage,
    Ssm5DtcRecord,
    Ssm5EcuRecord,
} from './dtos';
import type { DtcReadLineListener, VehicleLookupLineListener } from './ssm5-client';

import { Ssm5Client } from './ssm5-client';

const STEP_DURATION_MS = 1000;
const RECENT_LOOKUP_DURATION_MS = 400;
const STATUS_CHECK_DURATION_MS = 200;
const MOCK_VIN = 'JF2BURJDXTY453911';
const UNMATCHED_ID = '11111111-11111111';
const UNMATCHED_VIN = 'JF2ZZZZZZZZ000000';

/** The Cloud Connector ID whose connection is lost {@link LOSS_DELAY_MS} after it is established. */
const LOST_ID = '99999999-99999999';

/** How long the connection of {@link LOST_ID} lasts once established. */
const LOSS_DELAY_MS = 12000;

/** The Cloud Connector ID whose connection is open only once it has been paired through this client. */
const RE_PAIRED_ID = '77777777-77777777';

/** The Cloud Connector ID whose lookup streams its steps slowly, then completes. */
const SLOW_ID = '88888888-88888888';

/** The Cloud Connector ID whose lookup streams its steps slowly, then fails to pair. */
const SLOW_FAILING_ID = '88888888-00000000';

/**
 * How long each in-progress step of the slow IDs takes: their three steps outlast the SSM5 page's
 * expected connect duration once, but settle before it has passed twice.
 */
const SLOW_STEP_DURATION_MS = 15000;

/** How long each in-progress step takes for an ID; IDs not listed take {@link STEP_DURATION_MS}. */
const STEP_DURATION_MS_BY_ID: Readonly<Partial<Record<string, number>>> = {
    [SLOW_FAILING_ID]: SLOW_STEP_DURATION_MS,
    [SLOW_ID]: SLOW_STEP_DURATION_MS,
};

/** The recent connection of {@link MOCK_VIN}, and the technician's last one; its ID connects successfully. */
const WORKING_RECENT_CONNECTION: RecentConnectionRecord = {
    cloudConnectorId: 'AC3E4C9B-31C1FECB',
    connectedAt: '2026-06-30T15:12:00',
    vin: MOCK_VIN,
};

/** The recent connection of every VIN ending in {@link FAILING_RECENT_VIN_SUFFIX}; its ID fails to pair. */
const FAILING_RECENT_CONNECTION: RecentConnectionRecord = {
    cloudConnectorId: '00000000-00000000',
    connectedAt: '2026-06-29T09:30:00',
    vin: 'JF2AAAAAAAA000000',
};

const FAILING_RECENT_VIN_SUFFIX = '0';

/** The recent connection of every VIN ending in {@link RE_PAIRED_RECENT_VIN_SUFFIX}; its ID has to pair again. */
const RE_PAIRED_RECENT_CONNECTION: RecentConnectionRecord = {
    cloudConnectorId: RE_PAIRED_ID,
    connectedAt: '2026-06-28T14:05:00',
    vin: 'JF2AAAAAAAA000008',
};

const RE_PAIRED_RECENT_VIN_SUFFIX = '8';

/** The scripted recent connection by the VIN's last character, for VINs other than {@link MOCK_VIN}. */
const RECENT_CONNECTION_BY_VIN_SUFFIX: Readonly<Partial<Record<string, RecentConnectionRecord>>> = {
    [FAILING_RECENT_VIN_SUFFIX]: FAILING_RECENT_CONNECTION,
    [RE_PAIRED_RECENT_VIN_SUFFIX]: RE_PAIRED_RECENT_CONNECTION,
};

/** How long an ID's connection stays open once established through this client; IDs not listed stay open. */
const OPEN_FOR_MS_BY_ID: Readonly<Partial<Record<string, number>>> = {
    [LOST_ID]: LOSS_DELAY_MS,
    [RE_PAIRED_ID]: Number.POSITIVE_INFINITY,
};

/** The Cloud Connector IDs whose connection is never open, however and whenever it is checked. */
const NEVER_PAIRED_IDS: ReadonlySet<string> = new Set([FAILING_RECENT_CONNECTION.cloudConnectorId]);

/** The in-progress steps, as the endpoint streams them. */
const STEPS: readonly Pick<GetVehicleByCloudConnectorIdLine, 'message' | 'progress'>[] = [
    { message: 'Pairing.', progress: 0 },
    { message: 'Getting Vin.', progress: 20 },
    { message: 'Identifying Vehicle.', progress: 70 },
];

/** What a scripted read ends with: what it completes with, or the failure it ends with. */
type Scripted<TOutcome> =
    | TOutcome
    | {
          /** The failure the read ends with. */
          failure: ScriptedFailure;
      };

/** The fields of a failed last line that tell its error. */
type ScriptedFailure = Pick<GetVehicleByCloudConnectorIdLine, 'code' | 'error_id' | 'message'>;

/** The failure of an ID that cannot pair with its Cloud Connector. */
const PAIRING_FAILURE: ScriptedFailure = {
    code: 400,
    error_id: 300,
    message: 'I received an error from CloudConnector while I was pairing.',
};

/** Cloud Connector IDs the lookup fails on, with the failed line's code, error id and message. */
const FAILURE_BY_ID: Readonly<Record<string, ScriptedFailure>> = {
    '00000000-00000000': PAIRING_FAILURE,
    '22222222-22222222': {
        code: 400,
        error_id: 301,
        message: 'An error was received from CloudConnector while obtaining the VIN data from the car.',
    },
    '33333333-33333333': { code: 400, error_id: 201, message: 'Invalid length.' },
    '44444444-44444444': { code: 403, error_id: 100, message: 'Authorization error.' },
    '55555555-55555555': { code: 500, error_id: 10000, message: 'Errors on the system.' },
    '66666666-66666666': { code: 404, error_id: null, message: 'Cloud Connector ID not found.' },
    [SLOW_FAILING_ID]: PAIRING_FAILURE,
};

/** The in-progress steps of the DTC read, as the endpoint streams them. */
const DTC_READ_STEPS: readonly Pick<GetAllDtcsLine, 'message' | 'progress'>[] = [
    { message: 'Checking Battery Voltage.', progress: 0 },
    { message: 'Getting System Information.', progress: 30 },
    { message: 'Getting All DTC Information.', progress: 70 },
];

/** When the scripted diagnostics were created, in epoch milliseconds. */
const MEASURE_ID = '1728007854899';

/** The Cloud Connector ID whose vehicle has no DTC to report. */
const NO_DTCS_ID = 'DTCEMPTY-00000000';

/** The Cloud Connector ID whose first DTC read after each connect fails in a way a retry recovers from. */
const RETRY_ONCE_ID = 'DTCRETRY-00000000';

/** The Cloud Connector ID whose DTC reads always fail, each time in a way a retry could recover from. */
const RETRY_ALWAYS_ID = 'DTCRETRY-11111111';

/** The Cloud Connector ID whose DTC read fails in a way no retry recovers from. */
const UNAVAILABLE_DTCS_ID = 'DTCFATAL-00000000';

/** The Cloud Connector ID whose DTCs come back with missing, unreadable or incomplete fields. */
const PARTIAL_DTCS_ID = 'DTCPARTL-00000000';

/** The Cloud Connector ID whose DTC read lists a system the diagram read reports no module for. */
const UNMAPPED_SYSTEM_ID = 'DTCUNMAP-00000000';

/** A DTC read failure a retry can recover from: the Cloud Connector could not read the DTCs this time. */
const DTC_READ_FAILURE: ScriptedFailure = {
    code: 400,
    error_id: 303,
    message: 'An error was received from CloudConnector while attempting to acquire DTC data from the vehicle.',
};

/** A DTC read failure no retry recovers from: the vehicle information could not be obtained. */
const VEHICLE_INFORMATION_FAILURE: ScriptedFailure = {
    code: 400,
    error_id: 205,
    message: 'Vehicle information not obtained error.',
};

/** The fields a scripted DTC record sets; the rest are the same for every scripted record. */
type ScriptedDtc = Pick<
    Ssm5DtcRecord,
    | 'code'
    | 'description'
    | 'dtc_status'
    | 'ffd_count'
    | 'has_ffd'
    | 'igon_mill_sec'
    | 'igon_time'
    | 'is_chain_dtc'
    | 'related_documents_count'
    | 'system'
>;

const TORQUE_FAILURE_DTC: ScriptedDtc = {
    code: 'C1F4268',
    description: 'EGI Torque Failure Signal Event Information',
    dtc_status: 'current',
    ffd_count: 1,
    has_ffd: true,
    igon_mill_sec: 41800,
    igon_time: 576,
    is_chain_dtc: true,
    related_documents_count: 6,
    system: 'VDC EPB',
};

const TORQUE_DOWN_DTC: ScriptedDtc = {
    code: 'C1F4168',
    description: 'Forbidden State of EGI torque down event information',
    dtc_status: 'past',
    ffd_count: 3,
    has_ffd: true,
    igon_mill_sec: 0,
    igon_time: 576,
    is_chain_dtc: true,
    related_documents_count: 4,
    system: 'VDC EPB',
};

const EYESIGHT_DTC: ScriptedDtc = {
    code: 'B1F3068',
    description: 'EyeSight Event Information',
    dtc_status: 'current',
    ffd_count: 0,
    has_ffd: false,
    igon_mill_sec: 41400,
    igon_time: 576,
    is_chain_dtc: true,
    related_documents_count: 2,
    system: 'SNR',
};

const CAMSHAFT_BANK_1_DTC: ScriptedDtc = {
    code: 'P000B7C',
    description: '"B" Camshaft Position Bank 1 Slow Response',
    dtc_status: 'past',
    ffd_count: 3,
    has_ffd: true,
    igon_mill_sec: 44200,
    igon_time: 576,
    is_chain_dtc: false,
    related_documents_count: 3,
    system: 'EGI',
};

const CAMSHAFT_BANK_2_DTC: ScriptedDtc = {
    code: 'P000D7C',
    description: '"B" Camshaft Position Bank 2 Slow Response',
    dtc_status: 'past',
    ffd_count: 1,
    has_ffd: true,
    igon_mill_sec: 43500,
    igon_time: 576,
    is_chain_dtc: false,
    related_documents_count: 3,
    system: 'EGI',
};

/**
 * The DTCs of the design: seven codes across four systems, next to a system that reports none. The two
 * CCU codes sit below the fold of the design, which shows neither their documents nor their freeze frames.
 */
const DESIGN_DTCS: readonly ScriptedDtc[] = [
    systemWithoutDtc('CGW'),
    TORQUE_FAILURE_DTC,
    TORQUE_DOWN_DTC,
    EYESIGHT_DTC,
    CAMSHAFT_BANK_1_DTC,
    CAMSHAFT_BANK_2_DTC,
    {
        code: 'B2244',
        description: 'Amplifier Communication',
        dtc_status: 'past',
        ffd_count: 2,
        has_ffd: true,
        igon_mill_sec: 44200,
        igon_time: 576,
        is_chain_dtc: true,
        related_documents_count: 1,
        system: 'CCU',
    },
    {
        code: 'B22AB',
        description: 'Carplay / Android Auto Disconnect',
        dtc_status: 'past',
        ffd_count: 0,
        has_ffd: false,
        igon_mill_sec: 43500,
        igon_time: 576,
        is_chain_dtc: true,
        related_documents_count: 0,
        system: 'CCU',
    },
];

/**
 * A DTC of the shift-by-wire system, a system the backend knows no diagram cell for: the diagram read
 * leaves it out, as the backend does with a system it cannot map, while the DTC read lists it.
 */
const SHIFT_BY_WIRE_DTC: ScriptedDtc = {
    code: 'P0705',
    description: 'Transmission Range Sensor Circuit Malfunction (PRNDL Input)',
    dtc_status: 'current',
    ffd_count: 1,
    has_ffd: true,
    igon_mill_sec: 42000,
    igon_time: 576,
    is_chain_dtc: false,
    related_documents_count: 2,
    system: 'SBW',
};

/**
 * DTCs whose fields come back incomplete: a missing, an empty and an unreadable description, freeze
 * frame data reported absent despite a count, counts that cannot be read, and a record sent twice.
 */
const PARTIAL_DTCS: readonly ScriptedDtc[] = [
    { ...CAMSHAFT_BANK_1_DTC, description: null },
    { ...CAMSHAFT_BANK_2_DTC, description: '' },
    { ...TORQUE_FAILURE_DTC, description: '\uFFFD\uFFFD\uFFFD', ffd_count: null },
    { ...TORQUE_DOWN_DTC, ffd_count: 3, has_ffd: false },
    { ...EYESIGHT_DTC, ffd_count: -1, has_ffd: true },
    { ...EYESIGHT_DTC, ffd_count: -1, has_ffd: true },
];

/** What a scripted DTC read ends with: a failure, or the records it completes with. */
type ScriptedDtcRead = Scripted<{
    /** The records the read completes with. */
    dtcs: readonly ScriptedDtc[];
}>;

/** DTC reads scripted per Cloud Connector ID; IDs not listed complete with {@link DESIGN_DTCS}. */
const DTC_READ_BY_ID: Readonly<Record<string, ScriptedDtcRead>> = {
    [NO_DTCS_ID]: { dtcs: [] },
    [PARTIAL_DTCS_ID]: { dtcs: PARTIAL_DTCS },
    [RETRY_ALWAYS_ID]: { failure: DTC_READ_FAILURE },
    [UNAVAILABLE_DTCS_ID]: { failure: VEHICLE_INFORMATION_FAILURE },
    [UNMAPPED_SYSTEM_ID]: { dtcs: [...DESIGN_DTCS, SHIFT_BY_WIRE_DTC] },
};

/** The Cloud Connector ID whose diagram read fails. */
const FAILING_DIAGRAM_ID = 'DIAGFAIL-00000000';

/** The Cloud Connector ID whose vehicle has no drawing to show; its modules are the design's. */
const NO_DIAGRAM_ID = 'DIAGNONE-00000000';

/** Where the diagram file of {@link MOCK_VIN}, a 2026 Outback, is served from until the backend has a bucket. */
const DIAGRAM_SVG_URL = '/ssm5-diagrams/2026/OUTBACK/OUTBACK_2026MY.svg';

/** The failure of a diagram read the backend could not complete. */
const DIAGRAM_READ_FAILURE: ScriptedFailure = { code: 500, error_id: 10000, message: 'Errors on the system.' };

/** The fields a scripted module sets; its name, unit ids and reprogramming flag follow from its id. */
type ScriptedEcu = Pick<Ssm5EcuRecord, 'commStatus' | 'diagramEcuId' | 'dtcCount' | 'hasPrimaryDtc'> & {
    /** The DTCs the module reports, if any. */
    dtcs?: readonly ScriptedDtc[];
};

/** The battery reading the design shows in a module's General Info. */
const DESIGN_BATTERY_VOLTAGE: Ssm5BatteryVoltage = { is_valid: true, voltage: 12.2 };

/** The modules of the design's diagram that can be reprogrammed, with where SSM5 opens for each. */
const REPRO_URL_BY_ECU_ID: Readonly<Partial<Record<string, string>>> = {
    EGI: 'https://ssm5.example/reprogramming/EGI',
};

/** The calibration id and verification number the design shows, the same pair on each module it shows them for. */
const DESIGN_UNIT_IDS: Pick<Ssm5EcuRecord, 'cid' | 'cvn'> = { cid: 'LG7G300G00G', cvn: '7291C2AC' };

/** The calibration id and verification number of the design's modules, where the design shows them. */
const UNIT_IDS_BY_ECU_ID: Readonly<Partial<Record<string, Pick<Ssm5EcuRecord, 'cid' | 'cvn'>>>> = {
    EGI: DESIGN_UNIT_IDS,
    EYE: DESIGN_UNIT_IDS,
};

/** The diagram cell of each system the DTC read names differently from the diagram. */
const ECU_ID_BY_SYSTEM: Readonly<Partial<Record<string, string>>> = { 'VDC EPB': 'VDC' };

/** The module that never answers on the design's diagram. */
const UNANSWERED_ECU_ID = 'TCU';

/** The modules of the design's diagram that answer without a DTC: every other cell of the 2026 Outback. */
const CONNECTED_ECU_IDS: readonly string[] = [
    'ABG',
    'ACN',
    'AHL',
    'BIU',
    'BST',
    'C-ECU',
    'CGW',
    'DAM',
    'DCM',
    'DMS',
    'EAM',
    'EPS',
    'EYE',
    'FCR-L',
    'FCR-R',
    'FRM',
    'LCT',
    'MCA',
    'MED',
    'MEP',
    'MET',
    'MVS',
    'PRG',
    'PSM',
    'RCR-L',
    'RCR-R',
    'RKE',
    'SMT',
    'STA',
];

/** The name of each module id, as the backend's SSM5 DTC service spells it; an id it does not know is its own name. */
const DISPLAY_NAME_BY_ECU_ID: Readonly<Record<string, string>> = {
    ABG: 'Air Bag',
    ACN: 'Air Conditioning',
    AHL: 'Adaptive Headlight',
    ASC: 'Active Sound Control',
    BCCM: 'Battery Charging Control Module',
    BECM: 'Battery Energy Control Module',
    BIU: 'Battery Interface Unit',
    BST: 'Brake System',
    'C-ECU': 'Central ECU',
    CCU: 'Climate Control Unit',
    CGW: 'Central Gateway Module',
    CPLG: 'Coupling',
    DAM: 'Damage Management',
    DCM: 'Diagnostic Communication Module',
    DMCM: 'Drive Motor Control Module',
    DMS: 'Driver Monitoring System',
    EAM: 'Engine Acoustic Management',
    EGI: 'Engine (Electronic Gasoline Injection)',
    EPS: 'Electric Power Steering',
    EYE: 'Eye Sight System',
    'FCR-L': 'Front Camera Left',
    'FCR-R': 'Front Camera Right',
    FRM: 'Front Right Module',
    HPCM: 'High Power Conversion Module',
    IMB: 'Integrated Module Block',
    LCT: 'Light Control',
    MCA: 'Motor Control A',
    MED: 'Mirror Electronic Drive',
    MEP: 'Mirror Electronic Passenger',
    MET: 'Meter',
    MVS: 'Multi Vision System',
    PRG: 'Parking',
    PSM: 'Power Steering Module',
    PWD: 'Power Window/Door',
    'RCR-L': 'Rear Camera Left',
    'RCR-R': 'Rear Camera Right',
    RKE: 'Remote Key Entry',
    RLS: 'Run Light System',
    SBW: 'Steer By Wire',
    SNR: 'Sensor',
    STA: 'Steering Angle',
    TCU: 'Transmission Control Unit',
    TPM: 'Tire Pressure Monitoring',
    VDC: 'Vehicle Dynamic Control',
    VSS: 'Vehicle Speed Sensor',
};

/** The modules of the design's diagram as they report the design's DTCs. */
const DESIGN_ECUS: readonly ScriptedEcu[] = designEcusReporting(DESIGN_DTCS);

/** The modules of the design's diagram with every one of them answering without a DTC. */
const CLEAR_ECUS: readonly ScriptedEcu[] = DESIGN_ECUS.map(({ diagramEcuId }) => connectedEcu(diagramEcuId));

/** What a scripted diagram read ends with: a failure, or the modules it completes with and their drawing, if any. */
type ScriptedDiagramRead = Scripted<{
    /** The modules the read completes with. */
    ecus: readonly ScriptedEcu[];
    /** Where the drawing is; absent for a vehicle whose model has none. */
    svgUrl?: string;
}>;

/** The diagram read of the design's vehicle: its modules with its drawing. */
const DESIGN_DIAGRAM_READ: ScriptedDiagramRead = { ecus: DESIGN_ECUS, svgUrl: DIAGRAM_SVG_URL };

/**
 * Diagram reads scripted per Cloud Connector ID; IDs not listed complete with {@link DESIGN_DIAGRAM_READ}.
 * The modules of an ID whose DTC read is scripted report that read's DTCs, so its cards agree with its list.
 */
const DIAGRAM_READ_BY_ID: Readonly<Record<string, ScriptedDiagramRead>> = {
    [FAILING_DIAGRAM_ID]: { failure: DIAGRAM_READ_FAILURE },
    [NO_DIAGRAM_ID]: { ecus: DESIGN_ECUS },
    [NO_DTCS_ID]: { ecus: CLEAR_ECUS, svgUrl: DIAGRAM_SVG_URL },
    [PARTIAL_DTCS_ID]: { ecus: designEcusReporting(PARTIAL_DTCS), svgUrl: DIAGRAM_SVG_URL },
};

/** How long the scripted freeze frame read waits before deciding its answer. */
const FREEZE_FRAME_READ_DURATION_MS = 1000;

/** How long the simulated freeze frame document download takes. */
const FREEZE_FRAME_DOCUMENT_DURATION_MS = 1000;

/** The simulated document file of each contract format, independent of the search. */
const FREEZE_FRAME_DOCUMENT_BY_FORMAT: Readonly<
    Record<GetFreezeFramesDocumentRequest['format'], (request: GetFreezeFramesDocumentRequest) => Blob>
> = {
    CSV: ({ dtcCode, module }) =>
        new Blob([`Simulated FFD detail document\r\nModule,DTC\r\n${module},${dtcCode}\r\n`], {
            type: 'text/csv;charset=utf-8',
        }),
    PDF: ({ dtcCode, module }) =>
        new Blob([writeSimulatedFreezeFramePdf(`Simulated FFD detail document: ${module} ${dtcCode}`)], {
            type: 'application/pdf',
        }),
};

/** The Cloud Connector ID whose first freeze frame read of each DTC after each connect fails. */
const FREEZE_FRAME_RETRY_ONCE_ID = 'FFDRETRY-00000000';

/** The freeze frame error for data that is no longer stored. */
const FREEZE_FRAME_NOT_FOUND: GetFreezeFramesErrorResponse = {
    code: 'NOT_FOUND',
    details: null,
    message: 'The freeze frame data is no longer stored.',
};

/** The freeze frame error for a Cloud Connector that failed while reading. */
const FREEZE_FRAME_READ_FAILURE: GetFreezeFramesErrorResponse = {
    code: 'FREEZE_FRAME_READ_FAILED',
    details: null,
    message: 'The Cloud Connector failed while reading the freeze frame data.',
};

/** Freeze frame reads that always fail, checked before the retry-once script. */
const FREEZE_FRAME_ERROR_BY_ID: Readonly<Partial<Record<string, GetFreezeFramesErrorResponse>>> = {
    'FFDGONE0-00000000': FREEZE_FRAME_NOT_FOUND,
    'FFDRETRY-11111111': FREEZE_FRAME_READ_FAILURE,
};

/** A captured moment used as a key in the freeze frame script. */
type FreezeFrameMoment =
    'DETECT' | 'HISTORY_1' | 'HISTORY_2' | 'HISTORY_3' | 'HISTORY_4' | 'POST_1' | 'POST_2' | 'POST_3' | 'POST_4';

/** The frame each scripted moment represents. */
const FREEZE_FRAME_BY_MOMENT: Readonly<Record<FreezeFrameMoment, GetFreezeFramesFrameResponse>> = {
    DETECT: { kind: 'DETECT', sequence: null },
    HISTORY_1: { kind: 'HISTORY', sequence: 1 },
    HISTORY_2: { kind: 'HISTORY', sequence: 2 },
    HISTORY_3: { kind: 'HISTORY', sequence: 3 },
    HISTORY_4: { kind: 'HISTORY', sequence: 4 },
    POST_1: { kind: 'POST', sequence: 1 },
    POST_2: { kind: 'POST', sequence: 2 },
    POST_3: { kind: 'POST', sequence: 3 },
    POST_4: { kind: 'POST', sequence: 4 },
};

/** One scripted PID with its unit and the text captured at each moment. */
type ScriptedFreezeFrameParameter = {
    /** The PID name from the design. */
    name: string;
    /** The unit from the design, or null when absent. */
    unit: string | null;
    /** The value as text at each captured moment. */
    valuesByMoment: Readonly<Record<FreezeFrameMoment, string>>;
};

/** The eight PIDs of the design, with Time Count and Trip Count first and the rest by name. */
const DESIGN_FREEZE_FRAME_PARAMETERS: readonly ScriptedFreezeFrameParameter[] = [
    {
        name: 'Time Count',
        unit: 'ms',
        valuesByMoment: {
            DETECT: '1084700',
            HISTORY_1: '1084500',
            HISTORY_2: '1084000',
            HISTORY_3: '1083500',
            HISTORY_4: '1083000',
            POST_1: '1084800',
            POST_2: '1084900',
            POST_3: '1085000',
            POST_4: '1087000',
        },
    },
    {
        name: 'Trip Count',
        unit: null,
        valuesByMoment: {
            DETECT: '576',
            HISTORY_1: '576',
            HISTORY_2: '576',
            HISTORY_3: '576',
            HISTORY_4: '576',
            POST_1: '576',
            POST_2: '576',
            POST_3: '576',
            POST_4: '576',
        },
    },
    {
        name: 'A/F Sensor #1',
        unit: null,
        valuesByMoment: {
            DETECT: '0.97',
            HISTORY_1: '0.95',
            HISTORY_2: '0.95',
            HISTORY_3: '0.94',
            HISTORY_4: '0.95',
            POST_1: '0.95',
            POST_2: '0.95',
            POST_3: '0.95',
            POST_4: '0.95',
        },
    },
    {
        name: 'Absolute Load Value',
        unit: '%',
        valuesByMoment: {
            DETECT: '33.3',
            HISTORY_1: '20',
            HISTORY_2: '14.9',
            HISTORY_3: '49.8',
            HISTORY_4: '20',
            POST_1: '20',
            POST_2: '14.9',
            POST_3: '20',
            POST_4: '20',
        },
    },
    {
        name: 'Accel. Opening Angle',
        unit: '%',
        valuesByMoment: {
            DETECT: '10.6',
            HISTORY_1: '9.4',
            HISTORY_2: '0',
            HISTORY_3: '12.5',
            HISTORY_4: '9.4',
            POST_1: '9.4',
            POST_2: '0',
            POST_3: '9.4',
            POST_4: '9.4',
        },
    },
    {
        name: 'Actual Throttle Opening Angle',
        unit: 'deg',
        valuesByMoment: {
            DETECT: '8',
            HISTORY_1: '5',
            HISTORY_2: '4',
            HISTORY_3: '11',
            HISTORY_4: '5',
            POST_1: '5',
            POST_2: '4',
            POST_3: '5',
            POST_4: '5',
        },
    },
    {
        name: 'Ambient Temperature',
        unit: '°F',
        valuesByMoment: {
            DETECT: '64',
            HISTORY_1: '64',
            HISTORY_2: '64',
            HISTORY_3: '64',
            HISTORY_4: '64',
            POST_1: '64',
            POST_2: '64',
            POST_3: '64',
            POST_4: '64',
        },
    },
    {
        name: 'Atmosphere Pressure',
        unit: 'mmHg',
        valuesByMoment: {
            DETECT: '750',
            HISTORY_1: '750',
            HISTORY_2: '750',
            HISTORY_3: '750',
            HISTORY_4: '750',
            POST_1: '750',
            POST_2: '750',
            POST_3: '750',
            POST_4: '750',
        },
    },
];

/** How many PIDs every readout of the design carries. */
const DESIGN_FREEZE_FRAME_PARAMETER_COUNT = 130;

/** How many filler PIDs complete the design's count after its named rows. */
const FILLER_FREEZE_FRAME_PARAMETER_COUNT = DESIGN_FREEZE_FRAME_PARAMETER_COUNT - DESIGN_FREEZE_FRAME_PARAMETERS.length;

/** Filler PIDs only to reach the design's count of 130; they are not rows supplied by the design. */
const FILLER_FREEZE_FRAME_PARAMETERS = Array.from({ length: FILLER_FREEZE_FRAME_PARAMETER_COUNT }, (_, index) => {
    const number = DESIGN_FREEZE_FRAME_PARAMETERS.length + index + 1;

    return { name: `PID ${String(number).padStart(3, '0')}`, unit: null, value: String(number) };
});

/** The frames of one DTC, with any scripted value states overriding the design's valid values. */
type ScriptedFreezeFrameReadout = {
    /** The captured moments in the order the readout answers them. */
    moments: readonly FreezeFrameMoment[];
    /** Overrides by PID name and moment, for values that are empty or cannot be shown. */
    overrides?: Readonly<Partial<Record<string, ScriptedFreezeFrameValuesByMoment>>>;
};

/** The scripted readouts of one module by trouble code. */
type ScriptedFreezeFrameReadoutsByCode = Readonly<Partial<Record<string, ScriptedFreezeFrameReadout>>>;

/** The scripted value states of one PID by captured moment. */
type ScriptedFreezeFrameValuesByMoment = Readonly<Partial<Record<FreezeFrameMoment, GetFreezeFramesValueResponse>>>;

/** The readouts by module and trouble code, including the empty and invalid cells of C1F4268. */
const FREEZE_FRAME_READOUT_BY_DTC: Readonly<Partial<Record<string, ScriptedFreezeFrameReadoutsByCode>>> = {
    CCU: {
        B2244: { moments: ['DETECT', 'POST_1', 'POST_2', 'POST_3', 'POST_4'] },
    },
    EGI: {
        P000B7C: { moments: ['HISTORY_3', 'HISTORY_2', 'HISTORY_1', 'DETECT'] },
        P000D7C: { moments: ['DETECT'] },
    },
    'VDC EPB': {
        C1F4168: {
            moments: [
                'HISTORY_4',
                'HISTORY_3',
                'HISTORY_2',
                'HISTORY_1',
                'DETECT',
                'POST_1',
                'POST_2',
                'POST_3',
                'POST_4',
            ],
        },
        C1F4268: {
            moments: ['HISTORY_1', 'DETECT', 'POST_1'],
            overrides: {
                'A/F Sensor #1': {
                    HISTORY_1: { state: 'EMPTY', value: null },
                    POST_1: { state: 'INVALID', value: null },
                },
            },
        },
    },
};

/**
 * Stand-in for {@link Ssm5Client} until the SSM5 endpoints exist. Replays the vehicle lookup
 * stream deterministically: the IDs in {@link FAILURE_BY_ID} fail with their error, {@link UNMATCHED_ID}
 * reads a VIN no TechShare vehicle matches, every other ID completes with {@link MOCK_VIN}. Each
 * in-progress step takes {@link STEP_DURATION_MS}, except for the IDs in {@link STEP_DURATION_MS_BY_ID}:
 * {@link SLOW_ID} completes and {@link SLOW_FAILING_ID} fails to pair only after a long wait.
 * The recent-connection lookup answers {@link MOCK_VIN} with a working connection, VINs ending in
 * {@link FAILING_RECENT_VIN_SUFFIX} with one whose ID fails to pair, VINs ending in
 * {@link RE_PAIRED_RECENT_VIN_SUFFIX} with one whose ID has to pair again, and every other VIN with none.
 * The last-connection lookup always answers with the working connection. The status check reports
 * every ID as connected, except the {@link NEVER_PAIRED_IDS}, which are never connected, and the IDs in
 * {@link OPEN_FOR_MS_BY_ID}, which are gone until established through this client and stay open for the
 * listed time afterwards: {@link LOSS_DELAY_MS} for {@link LOST_ID}, for good for {@link RE_PAIRED_ID}.
 * The DTC read streams its steps, then completes with the DTCs of the design, except for the IDs in
 * {@link DTC_READ_BY_ID} and for {@link RETRY_ONCE_ID}, whose first read after each connect fails;
 * {@link UNMAPPED_SYSTEM_ID} adds a DTC of a system the diagram read reports no module for.
 * The diagram read takes as long as the DTC read's steps, then answers the file link of the design's
 * vehicle, its battery reading and the modules of {@link DESIGN_ECUS}, each with the DTCs it reports,
 * except for the IDs in {@link DIAGRAM_READ_BY_ID}: {@link NO_DTCS_ID} answers every module clear,
 * {@link PARTIAL_DTCS_ID} answers the modules reporting its own incomplete DTCs, {@link NO_DIAGRAM_ID}
 * answers the modules without a file link, {@link FAILING_DIAGRAM_ID} fails. Only the modules in
 * {@link REPRO_URL_BY_ECU_ID} can be reprogrammed.
 * The freeze frame read waits {@link FREEZE_FRAME_READ_DURATION_MS}, then answers the DTC's readout
 * from {@link FREEZE_FRAME_READOUT_BY_DTC}, or not found when absent. The IDs in
 * {@link FREEZE_FRAME_ERROR_BY_ID} always fail; {@link FREEZE_FRAME_RETRY_ONCE_ID} fails the first
 * read of each module and trouble code after each connect, then answers its readout.
 * The document download waits {@link FREEZE_FRAME_DOCUMENT_DURATION_MS}, then answers a small
 * simulated file of the requested format from {@link FREEZE_FRAME_DOCUMENT_BY_FORMAT}, ignoring search.
 */
export class MockSsm5Client extends Ssm5Client {
    /** When each ID last completed a lookup through this client. */
    private establishedAt = new Map<string, number>();

    /** When {@link FREEZE_FRAME_RETRY_ONCE_ID} was established before each DTC's read last failed; 0 before any lookup. */
    private freezeFrameRetryOnceFailedAfter = new Map<string, number>();

    /** When {@link RETRY_ONCE_ID} was established before its DTC read last failed; 0 before any lookup. */
    private retryOnceFailedAfter: number | undefined;

    /**
     * Stream the DTC read steps, then the scripted last line of the ID.
     *
     * @param request - the Cloud Connector ID
     * @param signal - aborts the read
     * @param onLine - receives every streamed line
     * @returns the last streamed line; rejects when aborted
     */
    override async getAllDtcs(
        { cloudConnectorId }: GetAllDtcsRequest,
        signal?: AbortSignal,
        onLine?: DtcReadLineListener
    ): Promise<GetAllDtcsResponse> {
        for (const step of DTC_READ_STEPS) {
            onLine?.({ ...step, code: 202, error_id: null, state: 'in-progress' });
            await wait(STEP_DURATION_MS, signal);
        }

        const lastLine = dtcReadLastLine(this.scriptedDtcRead(cloudConnectorId));
        onLine?.(lastLine);

        return lastLine;
    }

    /**
     * Answer with the technician's scripted last connection after a short delay.
     *
     * @param signal - aborts the lookup
     * @returns the working scripted connection; rejects when aborted
     */
    override async getLastConnection(signal?: AbortSignal): Promise<GetLastConnectionResponse> {
        await wait(RECENT_LOOKUP_DURATION_MS, signal);

        return { ...WORKING_RECENT_CONNECTION };
    }

    /**
     * Answer whether the connection is scripted as still open after a short delay.
     *
     * @param request - the Cloud Connector ID
     * @param signal - aborts the check
     * @returns the scripted status; rejects when aborted
     */
    override async getConnectionStatus(
        { cloudConnectorId }: GetConnectionStatusRequest,
        signal?: AbortSignal
    ): Promise<GetConnectionStatusResponse> {
        await wait(STATUS_CHECK_DURATION_MS, signal);

        return { isConnected: this.isConnectionOpen(cloudConnectorId) };
    }

    /**
     * Answer with the file link of the design's vehicle, its battery reading and the scripted modules of
     * the ID once the DTC read's steps would have passed.
     *
     * @param request - the Cloud Connector ID
     * @param signal - aborts the read
     * @returns the file link, the reading and the modules; rejects when aborted or when the read is scripted to fail
     */
    override async getDiagram(
        { cloudConnectorId }: GetDiagramRequest,
        signal?: AbortSignal
    ): Promise<GetDiagramResponse> {
        await wait(DTC_READ_STEPS.length * STEP_DURATION_MS, signal);

        const read = DIAGRAM_READ_BY_ID[cloudConnectorId] ?? DESIGN_DIAGRAM_READ;

        if ('failure' in read) {
            throw new Error(read.failure.message);
        }

        return {
            diagram: read.svgUrl === undefined ? {} : { svgUrl: read.svgUrl },
            ecus: read.ecus.map(ecuRecord),
            vehicle: { battery_voltage: DESIGN_BATTERY_VOLTAGE },
        };
    }

    /**
     * Answer with the scripted freeze frame readout or error after a short delay.
     *
     * @param request - the Cloud Connector ID, module and trouble code
     * @param signal - aborts the read
     * @returns the readout or a named contract error; rejects when aborted
     */
    override async getFreezeFrames(
        request: GetFreezeFramesRequest,
        signal?: AbortSignal
    ): Promise<GetFreezeFramesResponse> {
        await wait(FREEZE_FRAME_READ_DURATION_MS, signal);

        return this.scriptedFreezeFrameRead(request);
    }

    /**
     * Answer with a small simulated document after the document wait, ignoring the search.
     *
     * @param request - the session, DTC and requested format
     * @param signal - aborts the download
     * @returns the simulated file; rejects when aborted
     */
    override async getFreezeFramesDocument(
        request: GetFreezeFramesDocumentRequest,
        signal?: AbortSignal
    ): Promise<GetFreezeFramesDocumentResponse> {
        await wait(FREEZE_FRAME_DOCUMENT_DURATION_MS, signal);

        return FREEZE_FRAME_DOCUMENT_BY_FORMAT[request.format](request);
    }

    /**
     * Answer with the VIN's scripted recent connection after a short delay.
     *
     * @param request - the VIN
     * @param signal - aborts the lookup
     * @returns the scripted connection, or undefined when the VIN has none; rejects when aborted
     */
    override async getRecentConnectionByVin(
        { vin }: GetRecentConnectionByVinRequest,
        signal?: AbortSignal
    ): Promise<GetRecentConnectionByVinResponse> {
        await wait(RECENT_LOOKUP_DURATION_MS, signal);

        return scriptedRecentConnection(vin);
    }

    /**
     * Stream the in-progress steps on the ID's scripted timer, then the scripted last line.
     *
     * @param request - the Cloud Connector ID
     * @param signal - aborts the lookup
     * @param onLine - receives every streamed line
     * @returns the last streamed line; rejects when aborted
     */
    override async getVehicleByCloudConnectorId(
        { cloudConnectorId }: GetVehicleByCloudConnectorIdRequest,
        signal?: AbortSignal,
        onLine?: VehicleLookupLineListener
    ): Promise<GetVehicleByCloudConnectorIdResponse> {
        const stepDurationMs = STEP_DURATION_MS_BY_ID[cloudConnectorId] ?? STEP_DURATION_MS;

        for (const step of STEPS) {
            onLine?.({ ...step, ...lineBase(cloudConnectorId), code: 202, state: 'in-progress' });
            await wait(stepDurationMs, signal);
        }

        const lastLine = scriptedLastLine(cloudConnectorId);
        this.establishedAt.set(cloudConnectorId, Date.now());
        onLine?.(lastLine);

        return lastLine;
    }

    /**
     * Whether an ID's connection is scripted as open: never for the {@link NEVER_PAIRED_IDS}, only
     * for the time in {@link OPEN_FOR_MS_BY_ID} after being established for the IDs listed there,
     * always otherwise.
     *
     * @param cloudConnectorId - the Cloud Connector ID
     * @returns true while its connection is scripted to be open
     */
    private isConnectionOpen(cloudConnectorId: string): boolean {
        if (NEVER_PAIRED_IDS.has(cloudConnectorId)) {
            return false;
        }

        const openForMs = OPEN_FOR_MS_BY_ID[cloudConnectorId];

        return openForMs === undefined || this.isEstablishedWithin(cloudConnectorId, openForMs);
    }

    /**
     * Whether an ID completed a lookup through this client less than the given time ago.
     *
     * @param cloudConnectorId - the Cloud Connector ID
     * @param durationMs - how long after the lookup the connection is scripted to last
     * @returns true while its connection is scripted to last
     */
    private isEstablishedWithin(cloudConnectorId: string, durationMs: number): boolean {
        const establishedAt = this.establishedAt.get(cloudConnectorId);

        return establishedAt !== undefined && Date.now() - establishedAt < durationMs;
    }

    /**
     * The DTC read scripted for an ID. {@link RETRY_ONCE_ID} fails once per connect: its read fails when
     * no read failed since it was last established, and completes otherwise.
     *
     * @param cloudConnectorId - the Cloud Connector ID the read runs for
     * @returns what the read ends with
     */
    private scriptedDtcRead(cloudConnectorId: string): ScriptedDtcRead {
        if (cloudConnectorId !== RETRY_ONCE_ID) {
            return DTC_READ_BY_ID[cloudConnectorId] ?? { dtcs: DESIGN_DTCS };
        }

        const establishedAt = this.establishedAt.get(cloudConnectorId) ?? 0;

        if (this.retryOnceFailedAfter === establishedAt) {
            return { dtcs: DESIGN_DTCS };
        }

        this.retryOnceFailedAfter = establishedAt;

        return { failure: DTC_READ_FAILURE };
    }

    /**
     * The freeze frame answer of an ID, with the retry-once failure tracked per module and trouble code.
     *
     * @param request - the Cloud Connector ID, module and trouble code
     * @returns the scripted readout or error
     */
    private scriptedFreezeFrameRead(request: GetFreezeFramesRequest): GetFreezeFramesResponse {
        const error = FREEZE_FRAME_ERROR_BY_ID[request.cloudConnectorId];

        if (error) {
            return { ...error };
        }

        if (request.cloudConnectorId === FREEZE_FRAME_RETRY_ONCE_ID) {
            const establishedAt = this.establishedAt.get(request.cloudConnectorId) ?? 0;
            const key = JSON.stringify([request.module, request.dtcCode]);

            if (this.freezeFrameRetryOnceFailedAfter.get(key) !== establishedAt) {
                this.freezeFrameRetryOnceFailedAfter.set(key, establishedAt);

                return { ...FREEZE_FRAME_READ_FAILURE };
            }
        }

        const script = FREEZE_FRAME_READOUT_BY_DTC[request.module]?.[request.dtcCode];

        return script ? freezeFrameReadout(request, script) : { ...FREEZE_FRAME_NOT_FOUND };
    }
}

/**
 * A scripted module that answers without a DTC.
 *
 * @param diagramEcuId - the id of the module's cell
 * @returns the scripted module
 */
function connectedEcu(diagramEcuId: string): ScriptedEcu {
    return { commStatus: 'OK', diagramEcuId, dtcCount: 0, hasPrimaryDtc: false };
}

/**
 * The modules of the design's diagram as they report a DTC read: the systems the DTCs belong to, counted
 * per cell, then the one that never answers, then every other cell of the design answering without a DTC.
 *
 * @param dtcs - the scripted DTCs of the read
 * @returns the modules
 */
function designEcusReporting(dtcs: readonly ScriptedDtc[]): ScriptedEcu[] {
    const reporting = ecusReporting(dtcs);
    const reportingIds = new Set(reporting.map((ecu) => ecu.diagramEcuId));
    const designIds = [...ecusReporting(DESIGN_DTCS).map((ecu) => ecu.diagramEcuId), ...CONNECTED_ECU_IDS];

    return [
        ...reporting,
        { commStatus: 'ERROR', diagramEcuId: UNANSWERED_ECU_ID, dtcCount: 0, hasPrimaryDtc: false },
        ...designIds.filter((id) => !reportingIds.has(id)).map(connectedEcu),
    ];
}

/**
 * The last line of a DTC read: its scripted failure, or the completed DTC block. A read without any
 * record completes with an empty list, as the endpoint does for a vehicle with no system to report.
 *
 * @param read - what the read is scripted to end with
 * @returns the last line
 */
function dtcReadLastLine(read: ScriptedDtcRead): GetAllDtcsLine {
    if ('failure' in read) {
        return { ...read.failure, progress: 70, state: 'failed' };
    }

    return {
        code: 200,
        dtcs:
            read.dtcs.length === 0
                ? []
                : {
                      cgw_igon_mill_sec: '571600',
                      cgw_igon_time: '585',
                      cgw_status: 'current',
                      data: read.dtcs.map(dtcRecord),
                  },
        error_id: null,
        measure_id: MEASURE_ID,
        message: 'Completed',
        progress: 100,
        state: 'completed',
    };
}

/**
 * A full DTC record from its scripted fields.
 *
 * @param dtc - the scripted fields
 * @returns the record, with the fields every scripted record shares
 */
function dtcRecord(dtc: ScriptedDtc): Ssm5DtcRecord {
    return {
        ...dtc,
        form_group: 2,
        protocol_type: 'FHI_CAN',
        reference: dtc.system,
        type: dtc.code === null ? null : '1',
        unspecified_code: false,
    };
}

/**
 * A full module record from its scripted fields.
 *
 * @param ecu - the scripted fields
 * @returns the record, named from {@link DISPLAY_NAME_BY_ECU_ID}, with its DTCs as full records mapped to
 * its cell, the unit ids of {@link UNIT_IDS_BY_ECU_ID} and the reprogramming link of {@link REPRO_URL_BY_ECU_ID}
 */
function ecuRecord({ dtcs = [], ...ecu }: ScriptedEcu): Ssm5EcuRecord {
    return {
        ...ecu,
        cid: UNIT_IDS_BY_ECU_ID[ecu.diagramEcuId]?.cid ?? null,
        cvn: UNIT_IDS_BY_ECU_ID[ecu.diagramEcuId]?.cvn ?? null,
        displayName: DISPLAY_NAME_BY_ECU_ID[ecu.diagramEcuId] ?? ecu.diagramEcuId,
        dtcList: dtcs.map((dtc) => ({ ...dtcRecord(dtc), diagramEcuId: ecu.diagramEcuId })),
        reproAvailable: ecu.diagramEcuId in REPRO_URL_BY_ECU_ID,
        reproUrl: REPRO_URL_BY_ECU_ID[ecu.diagramEcuId] ?? null,
    };
}

/**
 * The modules the scripted DTCs belong to, one per diagram cell, with their DTCs listed and counted and
 * a primary one noted; a record without a code reports no module.
 *
 * @param dtcs - the scripted DTCs
 * @returns the modules, in the order their first DTC appears
 */
function ecusReporting(dtcs: readonly ScriptedDtc[]): ScriptedEcu[] {
    const ecus = new Map<string, ScriptedEcu>();
    const sent = new Set<string>();

    for (const dtc of dtcs) {
        const sentAs = JSON.stringify(dtc);

        // A record sent twice counts once, as the DTC read lists it once.
        if (dtc.code === null || sent.has(sentAs)) {
            continue;
        }

        sent.add(sentAs);

        const diagramEcuId = ECU_ID_BY_SYSTEM[dtc.system] ?? dtc.system;
        const ecu = ecus.get(diagramEcuId) ?? {
            commStatus: 'OK',
            diagramEcuId,
            dtcCount: 0,
            dtcs: [],
            hasPrimaryDtc: false,
        };

        ecus.set(diagramEcuId, {
            ...ecu,
            dtcCount: ecu.dtcCount + 1,
            dtcs: [...(ecu.dtcs ?? []), dtc],
            hasPrimaryDtc: ecu.hasPrimaryDtc || !dtc.is_chain_dtc,
        });
    }

    return Array.from(ecus.values());
}

/**
 * Build the parameters of a readout from the design's values, its overrides and the filler PIDs.
 *
 * @param script - the moments and value overrides of the readout
 * @returns the 130 PIDs with one value per moment
 */
function freezeFrameParameters(script: ScriptedFreezeFrameReadout): GetFreezeFramesParameterResponse[] {
    const design = DESIGN_FREEZE_FRAME_PARAMETERS.map(({ name, unit, valuesByMoment }) => ({
        name,
        unit,
        values: script.moments.map((moment): GetFreezeFramesValueResponse => ({
            ...(script.overrides?.[name]?.[moment] ?? { state: 'VALID', value: valuesByMoment[moment] }),
        })),
    }));
    const filler = FILLER_FREEZE_FRAME_PARAMETERS.map(({ name, unit, value }) => ({
        name,
        unit,
        values: script.moments.map((): GetFreezeFramesValueResponse => ({ state: 'VALID', value })),
    }));

    return design.concat(filler);
}

/**
 * Build the requested DTC's freeze frame readout from its scripted moments and values.
 *
 * @param request - the requested module and trouble code
 * @param script - the moments and value overrides of the readout
 * @returns the readout with the event's recorded time and location
 */
function freezeFrameReadout(
    { dtcCode, module }: GetFreezeFramesRequest,
    script: ScriptedFreezeFrameReadout
): GetFreezeFramesReadoutResponse {
    return {
        dtcCode,
        frames: script.moments.map((moment) => ({ ...FREEZE_FRAME_BY_MOMENT[moment] })),
        location: 'Seattle, WA',
        module,
        parameters: freezeFrameParameters(script),
        recordedAt: '2026-05-05T21:13:00Z',
    };
}

/**
 * The fields every streamed line carries.
 *
 * @param cloudConnectorId - the Cloud Connector ID the lookup runs for
 * @returns the shared fields
 */
function lineBase(
    cloudConnectorId: string
): Pick<GetVehicleByCloudConnectorIdLine, 'error_id' | 'id' | 'is_vin_changed'> {
    return { error_id: null, id: cloudConnectorId, is_vin_changed: false };
}

/**
 * The last line of the stream for the given ID: its scripted failure, or the completed vehicle.
 *
 * @param cloudConnectorId - the Cloud Connector ID the lookup runs for
 * @returns the last line
 */
function scriptedLastLine(cloudConnectorId: string): GetVehicleByCloudConnectorIdLine {
    const failure = FAILURE_BY_ID[cloudConnectorId];

    if (failure) {
        return { ...lineBase(cloudConnectorId), ...failure, progress: 70, state: 'failed' };
    }

    return {
        ...lineBase(cloudConnectorId),
        battery_voltage: { is_valid: true, voltage: 14 },
        code: 200,
        is_sold_vehicle: true,
        is_ssm5_supported: true,
        message: 'Completed.',
        progress: 100,
        project_id: '3710',
        spec: { classification: null, model: 'Outback', model_year: '2026' },
        state: 'completed',
        vin: {
            applied_model: null,
            classifications: null,
            type: 'vin',
            vin: cloudConnectorId === UNMATCHED_ID ? UNMATCHED_VIN : MOCK_VIN,
        },
    };
}

/**
 * The scripted recent connection of the given VIN, if it has one.
 *
 * @param vin - the VIN the lookup runs for
 * @returns the scripted connection, or undefined when the VIN has none
 */
function scriptedRecentConnection(vin: string): RecentConnectionRecord | undefined {
    if (vin === MOCK_VIN) {
        return { ...WORKING_RECENT_CONNECTION };
    }

    const recent = RECENT_CONNECTION_BY_VIN_SUFFIX[vin.slice(-1)];

    return recent && { ...recent, vin };
}

/**
 * The record of a system that reports no DTC.
 *
 * @param system - the system identifier
 * @returns the scripted fields of the record
 */
function systemWithoutDtc(system: string): ScriptedDtc {
    return {
        code: null,
        dtc_status: 'no_dtc',
        has_ffd: false,
        igon_mill_sec: null,
        igon_time: null,
        is_chain_dtc: false,
        system,
    };
}

/**
 * Write a one-page simulated PDF with character offsets for its ASCII objects.
 *
 * @param line - the simulated document line
 * @returns the PDF file text
 */
function writeSimulatedFreezeFramePdf(line: string): string {
    const escapedLine = line.replace(/[\\()]/g, '\\$&');
    const stream = `BT /F1 16 Tf 72 720 Td (${escapedLine}) Tj ET`;
    const objects = [
        '<< /Type /Catalog /Pages 2 0 R >>',
        '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
        '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>',
        `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
        '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    ];
    const header = '%PDF-1.4\n';
    const bodies = objects.map((object, index) => `${index + 1} 0 obj\n${object}\nendobj\n`);
    const offsets = bodies.map((_, index) =>
        bodies.slice(0, index).reduce((offset, body) => offset + body.length, header.length)
    );
    const fileText = header + bodies.join('');
    const xrefOffset = fileText.length;
    const entries = offsets.map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`).join('');

    return `${fileText}xref\n0 6\n0000000000 65535 f \n${entries}trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
}
